import {
  activeTab,
  stripIsWorthDrawing,
  tt,
  useAuth,
  useFranchiseModules,
  visibleTabs,
} from '@agos/shared'
import { EmptyState, TabGroup } from '@agos/ui'
import { Building2 } from 'lucide-react'
import { useMemo, type ComponentType } from 'react'
import { useSearchParams } from 'wouter'

import { CompaniesBody } from './CompaniesPage'
import { EXTERNAL_COMPANY_TABS } from './external-companies'
import { SuppliersPage } from './SuppliersPage'

/**
 * `/suppliers` - *Externé Firmy*, FRONTEND.md §9.347, the owner's C2.
 *
 * *"can you rename … to something like «Externé Firmy», and there you can have «Dodávatelia» and
 * «Firmy»"*. Two kinds of company: who the kitchen buys from, and who the restaurant invoices.
 *
 * **No `PageHeader` of its own, and that is the difference from `DeliveryHubPage`.** There the two
 * tabs are one screen's settings, so a header on each read the same title twice. Here they are two
 * different lists, and each keeps saying which list it is - `SuppliersPage` is 760 lines whose header
 * carries its own actions, and composing it whole means every spec that drives *Dodávatelia* keeps
 * driving the same component. The section name lives in the sidebar, where `RENAMED` puts it.
 *
 * **Both routes still answer on their own** - §9.208's rule, *"a hub cannot be a wall in front of a
 * page somebody has bookmarked"*. The tab is written into `?tab=`, so *Externé Firmy → Firmy* is
 * something one person can send another.
 */
const BODIES: Record<string, ComponentType> = {
  '/suppliers': SuppliersPage,
  '/invoice-companies': CompaniesBody,
}

export function ExternalCompaniesPage() {
  const { hasPermission, user } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()
  const [params, setParams] = useSearchParams()

  const mine = useMemo(
    () =>
      visibleTabs(EXTERNAL_COMPANY_TABS, {
        systemRole: user?.systemRole,
        hasPermission,
        isModuleEnabled,
      }),
    [user?.systemRole, hasPermission, isModuleEnabled],
  )

  const active = activeTab(mine, params.get('tab'))
  const current = mine.find((tab) => tab.id === active)
  const Body = current === undefined ? undefined : BODIES[current.path]

  return (
    <div className="space-y-4" data-external-companies={mine.length}>
      {/* One tab is not a choice, and a strip of one implies a second this employee cannot have. */}
      {stripIsWorthDrawing(mine) && (
        <TabGroup
          variant="bar"
          tabs={mine.map((tab) => ({ id: tab.id, label: tab.label }))}
          active={active ?? ''}
          onChange={(id) => {
            const next = new URLSearchParams(params)
            next.set('tab', id)
            setParams(next, { replace: true })
          }}
        />
      )}

      {Body === undefined ? (
        // Reachable: the hub gates on either permission, so somebody holding both with both modules
        // off arrives here. §9.302 recorded the same residual case and the same answer - the guard
        // shape cannot express *(permission AND module) OR (permission AND module)*.
        <EmptyState
          icon={<Building2 className="w-10 h-10" />}
          title={tt('Žiadne zoznamy')}
          description={tt('Váš balíček neobsahuje ani dodávateľov, ani vydané faktúry.')}
        />
      ) : (
        <Body />
      )}
    </div>
  )
}
